import type { RefObject } from "react";
import type { FieldErrors } from "react-hook-form";

// react-collapse animates for 250ms; wait past it so the expansion and the scroll don't overlap.
const COLLAPSE_ANIMATION_DELAY_MS = 450;
const SETTLE_DELAY_MS = 50;

/** Flattens nested react-hook-form errors into dot-paths, e.g. ["title", "attribute_values.2"]. */
export function flattenErrorPaths(errors: FieldErrors, prefix = ""): string[] {
  const paths: string[] = [];

  for (const key of Object.keys(errors)) {
    const value = (errors as Record<string, unknown>)[key];
    if (!value || typeof value !== "object") continue;

    const record = value as Record<string, unknown>;
    const fullPath = prefix ? `${prefix}.${key}` : key;
    const isLeaf =
      typeof record.message === "string" || typeof record.type === "string" || "ref" in record;

    if (isLeaf) paths.push(fullPath);
    else paths.push(...flattenErrorPaths(record as FieldErrors, fullPath));
  }

  return paths;
}

// Input renders border-red-600 and MaskInput delegates to it, so this covers every text primitive.
const ERRORED_INPUT_SELECTOR = "input.border-red-600, textarea.border-red-600";

function findErrorElement(fieldPath: string, container: HTMLElement): HTMLElement | null {
  // Attribute rows carry their field path as an id.
  const byId = document.getElementById(fieldPath);
  if (byId && container.contains(byId)) return byId;

  const byName = container.querySelector<HTMLElement>(`[name="${fieldPath}"]`);
  if (byName) return byName;

  return (
    container.querySelector<HTMLElement>(ERRORED_INPUT_SELECTOR) ??
    container.querySelector<HTMLElement>('[aria-invalid="true"]')
  );
}

/** A collapsible section that may be hiding the first error. */
export type ErrorSection = {
  isOpen: boolean;
  open: () => void;
};

/**
 * Expands the section holding the first error (when collapsed), then scrolls that field into view.
 *
 * Callers resolve their own section from `errorPaths[0]` — this knows nothing about any one form's
 * accordion layout.
 */
export function scrollToFirstError(
  errorPaths: string[],
  scrollContainerRef: RefObject<HTMLElement | null>,
  section?: ErrorSection
): void {
  if (errorPaths.length === 0) return;

  const firstErrorPath = errorPaths[0];
  const needsExpansion = !!section && !section.isOpen;

  if (needsExpansion) section.open();

  setTimeout(
    () => {
      // rAF so the browser has painted the expansion before the scroll animation starts.
      requestAnimationFrame(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const errorElement = findErrorElement(firstErrorPath, scrollContainer);
        if (!errorElement) return;

        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });

        if (
          errorElement instanceof HTMLInputElement ||
          errorElement instanceof HTMLTextAreaElement
        ) {
          errorElement.focus({ preventScroll: true });
        }
      });
    },
    needsExpansion ? COLLAPSE_ANIMATION_DELAY_MS : SETTLE_DELAY_MS
  );
}
